import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Truck, Package, RefreshCw, Clock, CheckSquare, AlertTriangle,
  MapPin, Phone, User, ChevronDown, ChevronUp, Check, X,
  Calendar, Zap, ArrowRight, Wrench, RotateCcw, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateWithDay } from '../utils/dateUtils';

import { getToken } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUTS = {
  a_preparer:       { label: 'À préparer',      color: '#9CA3AF', bg: 'rgba(156,163,175,0.1)', next: 'en_preparation' },
  en_preparation:   { label: 'En préparation',  color: 'var(--admin-amber)', bg: 'rgba(245,158,11,0.1)',  next: 'pret_depart' },
  pret_depart:      { label: 'Prêt au départ',  color: '#3B82F6', bg: 'rgba(59,130,246,0.1)',  next: 'en_route' },
  en_route:         { label: 'En route',         color: 'var(--admin-purple)', bg: 'rgba(139,92,246,0.1)', next: 'livre' },
  livre:            { label: 'Livré',            color: '#22C55E', bg: 'rgba(34,197,94,0.12)', next: 'installe' },
  installe:         { label: 'Installé ✓',      color: '#16A34A', bg: 'rgba(22,163,74,0.1)',  next: 'retour_prevu' },
  retour_prevu:     { label: 'Retour prévu',    color: '#F97316', bg: 'rgba(249,115,22,0.1)', next: 'retour_recupere' },
  retour_recupere:  { label: 'Retour récupéré', color: 'var(--admin-gold)', bg: 'rgba(212,175,55,0.1)', next: 'nettoyage' },
  nettoyage:        { label: 'En nettoyage',    color: '#06B6D4', bg: 'rgba(6,182,212,0.1)',  next: 'disponible' },
  disponible:       { label: 'Disponible ✓',    color: '#22C55E', bg: 'rgba(34,197,94,0.08)', next: null },
};

const CHECKLIST_TYPE_ICONS = {
  materiel:    { icon: Package,       color: 'var(--admin-teal)' },
  chargement:  { icon: Truck,         color: 'var(--admin-purple)' },
  photo:       { icon: ClipboardList, color: '#F4A7B2' },
  admin:       { icon: CheckSquare,   color: 'var(--admin-gold)' },
  nettoyage:   { icon: RotateCcw,     color: '#06B6D4' },
};

function StatCard({ label, value, color, icon: Icon, sublabel }) {
  return (
    <div className="card-premium p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} style={{ color }} />
        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{label}</p>
      </div>
      <p className="font-display text-3xl font-bold" style={{ color }}>{value}</p>
      {sublabel && <p className="text-xs mt-0.5" style={{ color: 'var(--admin-muted)' }}>{sublabel}</p>}
    </div>
  );
}

function FicheCard({ fiche, onStatutChange, onToggleChecklist }) {
  const [open, setOpen] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const statut = STATUTS[fiche.statut_logistique || fiche.statut] || STATUTS.a_preparer;
  const nextStatut = statut.next;
  const nextStatutConf = nextStatut ? STATUTS[nextStatut] : null;
  const progress = fiche.checklist_progress ?? 0;

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ border: `1px solid ${open ? statut.color + '40' : 'var(--border)'}`, background: '#FFFFFF' }}>

      {/* Header fiche */}
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 text-left"
        style={{ cursor: 'pointer', background: 'none', border: 'none' }}>

        {/* Statut pill */}
        <div className="flex-shrink-0">
          <span className="px-2.5 py-1.5 rounded-xl text-xs font-bold"
            style={{ background: statut.bg, color: statut.color }}>
            {statut.label}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{fiche.client_nom}</span>
            {fiche.chauffeur && (
              <span className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: 'var(--admin-teal-dim)', color: '#0B4B4E' }}>
                🚗 {fiche.chauffeur}
              </span>
            )}
          </div>
          <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>
            📍 {fiche.adresse || '?'} · {fiche.heure_livraison || fiche.heure_debut || ''} · {fiche.nb_products || fiche.products?.length || 0} produits
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Progress ring */}
          <div className="relative w-10 h-10">
            <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="14" fill="none" stroke="var(--border)" strokeWidth="3" />
              <circle cx="18" cy="18" r="14" fill="none"
                stroke={progress >= 100 ? '#22C55E' : '#D4AF37'} strokeWidth="3"
                strokeDasharray={`${progress * 0.88} 88`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold"
              style={{ color: 'var(--admin-text)' }}>{progress}%</span>
          </div>
          {open ? <ChevronUp size={16} style={{ color: 'var(--admin-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--admin-muted)' }} />}
        </div>
      </button>

      {/* Contenu développé */}
      {open && (
        <div className="border-t px-4 pb-4" style={{ borderColor: 'var(--admin-border)' }}>
          <div className="pt-4 grid sm:grid-cols-2 gap-4">

            {/* Infos client */}
            <div>
              <p className="text-xs font-bold mb-2" style={{ color: 'var(--admin-text)' }}>📋 Détails événement</p>
              <div className="space-y-1.5 text-xs" style={{ color: 'var(--admin-muted)' }}>
                {fiche.client_telephone && (
                  <p className="flex items-center gap-1.5">
                    <Phone size={11} />
                    <a href={`tel:${fiche.client_telephone}`} style={{ color: 'var(--admin-teal)' }}>{fiche.client_telephone}</a>
                  </p>
                )}
                <p className="flex items-center gap-1.5"><Calendar size={11} /> {fiche.date_evenement} {fiche.heure_debut && `· ${fiche.heure_debut}`}</p>
                <p className="flex items-center gap-1.5"><Truck size={11} /> Livraison : {fiche.date_livraison} {fiche.heure_livraison && `à ${fiche.heure_livraison}`}</p>
                {fiche.date_reprise && <p className="flex items-center gap-1.5"><RotateCcw size={11} /> Reprise : {fiche.date_reprise} {fiche.heure_reprise && `à ${fiche.heure_reprise}`}</p>}
                {fiche.lieu_type && <p className="flex items-center gap-1.5"><MapPin size={11} /> {fiche.lieu_type}</p>}
                {fiche.acces && <p className="flex items-center gap-1.5"><AlertTriangle size={11} /> Accès : {fiche.acces}</p>}
                {fiche.notes_client && <p className="italic mt-1">💬 "{fiche.notes_client}"</p>}
              </div>

              {/* Produits */}
              <p className="text-xs font-bold mt-3 mb-1" style={{ color: 'var(--admin-text)' }}>📦 Matériel ({fiche.items?.length || 0} articles)</p>
              <div className="space-y-1">
                {(fiche.items || []).map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-xs px-2 py-1 rounded-lg"
                    style={{ background: 'var(--admin-bg)' }}>
                    <span style={{ color: 'var(--admin-text)' }}>{item.product_nom}</span>
                    <span className="font-bold" style={{ color: 'var(--admin-teal)' }}>×{item.quantite || 1}</span>
                  </div>
                ))}
              </div>

              {/* Changer statut */}
              <div className="mt-4">
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--admin-text)' }}>🔄 Statut logistique</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(STATUTS).map(([key, conf]) => (
                    <button key={key}
                      onClick={() => onStatutChange(fiche.id || fiche.fiche_id, key)}
                      className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                      style={{
                        background: (fiche.statut_logistique || fiche.statut) === key ? conf.color : conf.bg,
                        color: (fiche.statut_logistique || fiche.statut) === key ? '#FFFFFF' : conf.color,
                        border: `1px solid ${conf.color}40`,
                        cursor: 'pointer',
                      }}>
                      {conf.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold" style={{ color: 'var(--admin-text)' }}>✅ Checklist préparation</p>
                <button onClick={() => setShowChecklist(!showChecklist)}
                  className="text-xs font-semibold" style={{ color: 'var(--admin-teal)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {showChecklist ? 'Masquer' : 'Voir tout'}
                </button>
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {(fiche.checklist || []).filter((_, i) => showChecklist || i < 6).map(item => {
                  const typeConf = CHECKLIST_TYPE_ICONS[item.type] || CHECKLIST_TYPE_ICONS.materiel;
                  const TypeIcon = typeConf.icon;
                  return (
                    <button key={item.id}
                      onClick={() => onToggleChecklist(fiche.id || fiche.fiche_id, item.id, !item.done)}
                      className="w-full flex items-start gap-2 p-2 rounded-lg text-left"
                      style={{ background: item.done ? 'rgba(34,197,94,0.06)' : 'var(--bg)', border: 'none', cursor: 'pointer' }}>
                      <div className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ borderColor: item.done ? '#22C55E' : 'var(--border)', background: item.done ? '#22C55E' : 'transparent' }}>
                        {item.done && <Check size={9} color="white" />}
                      </div>
                      <TypeIcon size={11} style={{ color: typeConf.color, flexShrink: 0, marginTop: 2 }} />
                      <span className="text-xs" style={{ color: item.done ? 'var(--text-muted)' : 'var(--ink)', textDecoration: item.done ? 'line-through' : 'none' }}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Bouton avancer statut */}
              {nextStatutConf && (
                <button
                  onClick={() => onStatutChange(fiche.id || fiche.fiche_id, nextStatut)}
                  className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold"
                  style={{ background: nextStatutConf.color, color: 'var(--admin-text)', border: 'none', cursor: 'pointer' }}>
                  Passer à : {nextStatutConf.label} →
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminExploitation() {
  const [data, setData] = useState(null);
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('today');
  const [filtreStatut, setFiltreStatut] = useState('all');

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true); else setRefreshing(true);
    try {
      const [today, fichesRes] = await Promise.all([
        fetch(`${BACKEND}/api/admin/exploitation/today`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/exploitation/fiches?limit=100`, { headers }).then(r => r.json()),
      ]);
      setData(today);
      setFiches(fichesRes.fiches || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); setRefreshing(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatutChange = async (ficheId, newStatut) => {
    try {
      await fetch(`${BACKEND}/api/admin/exploitation/fiche/${ficheId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ statut: newStatut }),
      });
      toast.success(`Statut mis à jour : ${STATUTS[newStatut]?.label}`);
      // Mettre à jour localement
      setFiches(prev => prev.map(f => f.id === ficheId ? { ...f, statut: newStatut } : f));
      setData(prev => {
        if (!prev) return prev;
        const update = (arr) => arr.map(f => (f.fiche_id === ficheId || f.id === ficheId) ? { ...f, statut_logistique: newStatut } : f);
        return {
          ...prev,
          departs_today: update(prev.departs_today || []),
          departs_tomorrow: update(prev.departs_tomorrow || []),
          a_preparer: update(prev.a_preparer || []),
        };
      });
    } catch { toast.error('Erreur mise à jour'); }
  };

  const handleToggleChecklist = async (ficheId, itemId, done) => {
    try {
      await fetch(`${BACKEND}/api/admin/exploitation/fiche/${ficheId}/checklist/${itemId}?done=${done}`, {
        method: 'PATCH', headers,
      });
      setFiches(prev => prev.map(f => {
        if (f.id !== ficheId) return f;
        const checklist = (f.checklist || []).map(c => c.id === itemId ? { ...c, done } : c);
        const progress = Math.round(checklist.filter(c => c.done).length / checklist.length * 100);
        return { ...f, checklist, checklist_progress: progress };
      }));
    } catch { }
  };

  const today = formatDateWithDay(new Date());

  const filteredFiches = filtreStatut === 'all' ? fiches : fiches.filter(f => f.statut === filtreStatut);

  const TABS = [
    { id: 'today', label: "Aujourd'hui", badge: data?.stats?.departs_today || 0 },
    { id: 'tomorrow', label: 'Demain', badge: data?.stats?.departs_tomorrow || 0 },
    { id: 'retours', label: 'Retours', badge: data?.stats?.retours_today || 0 },
    { id: 'preparation', label: 'À préparer', badge: data?.stats?.a_preparer || 0 },
    { id: 'toutes', label: 'Toutes les fiches', badge: fiches.length },
  ];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Retours</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Exploitation terrain</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Contrôle retours et état matériel</p>
      </div>
 className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--admin-teal-dim)' }}>
              <Truck size={20} style={{ color: 'var(--admin-teal)' }} />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold" style={{ color: 'var(--admin-text)' }}>Exploitation Terrain</h1>
              <p className="text-xs capitalize" style={{ color: 'var(--admin-muted)' }}>{today}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load(false)} disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
            style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>
            <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} /> Actualiser
          </button>
          <Link to="/admin/disponibilites"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
            style={{ background: '#0B1E20', color: 'var(--admin-gold)' }}>
            <Calendar size={14} /> Calendrier
          </Link>
        </div>
      </div>

      {/* Stats KPI */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatCard label="Départs aujourd'hui" value={data.stats.departs_today} color="#5DBCBF" icon={Truck} />
          <StatCard label="Départs demain" value={data.stats.departs_tomorrow} color="#D4AF37" icon={Calendar} />
          <StatCard label="Retours aujourd'hui" value={data.stats.retours_today} color="#F97316" icon={RotateCcw} />
          <StatCard label="À préparer" value={data.stats.a_preparer} color="#F59E0B" icon={Package} />
          <StatCard label="Matériel en maint." value={data.stats.materiel_maintenance} color="#EF4444" icon={Wrench} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b mb-5 overflow-x-auto" style={{ borderColor: 'var(--admin-border)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 flex-shrink-0"
            style={{
              borderColor: activeTab === tab.id ? '#D4AF37' : 'transparent',
              color: activeTab === tab.id ? '#D4AF37' : 'var(--text-muted)',
              background: 'none', cursor: 'pointer',
            }}>
            {tab.label}
            {tab.badge > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--admin-amber)' }}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: 'var(--border)' }} />)}</div>
      ) : (
        <div className="space-y-3">

          {/* Onglet Aujourd'hui */}
          {activeTab === 'today' && (
            <>
              {(data?.departs_today || []).length === 0 ? (
                <EmptyState icon={Truck} label="Aucun départ aujourd'hui" />
              ) : (
                (data.departs_today).map(f => (
                  <FicheCard key={f.id} fiche={f} onStatutChange={handleStatutChange} onToggleChecklist={handleToggleChecklist} />
                ))
              )}
            </>
          )}

          {/* Onglet Demain */}
          {activeTab === 'tomorrow' && (
            <>
              {(data?.departs_tomorrow || []).length === 0 ? (
                <EmptyState icon={Calendar} label="Aucun départ demain" />
              ) : (
                (data.departs_tomorrow).map(f => (
                  <FicheCard key={f.id} fiche={f} onStatutChange={handleStatutChange} onToggleChecklist={handleToggleChecklist} />
                ))
              )}
            </>
          )}

          {/* Onglet Retours */}
          {activeTab === 'retours' && (
            <>
              {(data?.retours_today || []).length === 0 && (data?.retours_this_week || []).length === 0 ? (
                <EmptyState icon={RotateCcw} label="Aucun retour prévu" />
              ) : (
                <>
                  {(data?.retours_today || []).length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-bold mb-2" style={{ color: '#F97316' }}>⚡ Retours aujourd'hui</p>
                      {data.retours_today.map(f => (
                        <FicheCard key={f.id} fiche={f} onStatutChange={handleStatutChange} onToggleChecklist={handleToggleChecklist} />
                      ))}
                    </div>
                  )}
                  {(data?.retours_this_week || []).length > 0 && (
                    <div>
                      <p className="text-xs font-bold mb-2" style={{ color: 'var(--admin-muted)' }}>Cette semaine</p>
                      {data.retours_this_week.map(f => (
                        <FicheCard key={f.id} fiche={f} onStatutChange={handleStatutChange} onToggleChecklist={handleToggleChecklist} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Onglet À préparer */}
          {activeTab === 'preparation' && (
            <>
              {(data?.a_preparer || []).length === 0 ? (
                <EmptyState icon={Package} label="Tout est prêt !" sub="Aucune commande à préparer cette semaine." />
              ) : (
                data.a_preparer.map(f => (
                  <FicheCard key={f.id} fiche={f} onStatutChange={handleStatutChange} onToggleChecklist={handleToggleChecklist} />
                ))
              )}
            </>
          )}

          {/* Onglet Toutes les fiches */}
          {activeTab === 'toutes' && (
            <>
              {/* Filtre par statut */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={() => setFiltreStatut('all')}
                  className="px-3 py-1.5 rounded-xl text-xs font-semibold"
                  style={{ background: filtreStatut === 'all' ? '#D4AF37' : 'var(--bg)', color: filtreStatut === 'all' ? '#0B1E20' : 'var(--text-muted)', border: `1px solid ${filtreStatut === 'all' ? '#D4AF37' : 'var(--border)'}`, cursor: 'pointer' }}>
                  Tous ({fiches.length})
                </button>
                {Object.entries(STATUTS).map(([k, v]) => {
                  const count = fiches.filter(f => f.statut === k).length;
                  if (count === 0) return null;
                  return (
                    <button key={k} onClick={() => setFiltreStatut(k)}
                      className="px-3 py-1.5 rounded-xl text-xs font-semibold"
                      style={{ background: filtreStatut === k ? v.color : v.bg, color: filtreStatut === k ? '#FFFFFF' : v.color, border: `1px solid ${v.color}40`, cursor: 'pointer' }}>
                      {v.label} ({count})
                    </button>
                  );
                })}
              </div>
              {filteredFiches.length === 0 ? (
                <EmptyState icon={ClipboardList} label="Aucune fiche" />
              ) : (
                filteredFiches.map(f => (
                  <FicheCard key={f.id} fiche={{ ...f, fiche_id: f.id, checklist_progress: f.checklist ? Math.round(f.checklist.filter(c => c.done).length / Math.max(f.checklist.length, 1) * 100) : 0 }}
                    onStatutChange={handleStatutChange} onToggleChecklist={handleToggleChecklist} />
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, label, sub }) {
  return (
    <div className="text-center py-12">
      <Icon size={36} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
      <p className="font-semibold text-sm" style={{ color: 'var(--admin-muted)' }}>{label}</p>
      {sub && <p className="text-xs mt-1" style={{ color: 'var(--admin-muted)' }}>{sub}</p>}
    </div>
  );
}
